import React, { useState, useEffect } from 'react';
import { assetUrl } from '../../utils/assets';
import { Card, UpgradeOffer } from '../../types';
import { useSoundEffects } from '../../context/SoundEffectsContext';
import TierIndicator from './TierIndicator';
import './UpgradeModal.css';

interface UpgradeModalProps {
  upgradeOffer: UpgradeOffer;
  triggeringCard: Card;
  isOpen: boolean;
  onSelectUpgrade: (selectedCardName: string) => void;
}

const formatCardName = (name: string): string => {
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  upgradeOffer,
  triggeringCard,
  isOpen,
  onSelectUpgrade
}) => {
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const { playSound } = useSoundEffects();

  useEffect(() => {
    if (isOpen) {
      playSound('new_card_available');
      setSelectedCard(null);
    }
  }, [isOpen, playSound]);

  if (!isOpen || !upgradeOffer) {
    return null;
  }

  const handleCardClick = (cardName: string) => {
    playSound('card_flip');
    setSelectedCard(cardName);
  };

  const handleConfirm = () => {
    if (selectedCard) {
      onSelectUpgrade(selectedCard);
    }
  };

  return (
    <div className="upgrade-overlay">
      <div className="upgrade-modal">
        <div className="upgrade-header">
          <h2>All that '{triggeringCard.verb?.toUpperCase()}'ing paid off!</h2>
          <p>Choose your new ability</p>
        </div>

        <div className="upgrade-content">
          <div className="upgrade-source">
            <div className="upgrade-source-card">
              <div className="upgrade-card-title" style={{ backgroundColor: '#7c9885' }}>
                {formatCardName(triggeringCard.name)}
              </div>
              <div className="upgrade-card-artwork">
                <img
                  src={assetUrl(`/assets/cards/${triggeringCard.name}.png`)}
                  alt={triggeringCard.name}
                  className="upgrade-card-image"
                />
              </div>
              {triggeringCard.tier && triggeringCard.tier !== 'tier_3' && (
                <TierIndicator
                  tier={triggeringCard.tier}
                  usageCount={triggeringCard.usage_count ?? 0}
                />
              )}
            </div>
            <div className="upgrade-arrow">
              <svg viewBox="0 0 24 24" width="40" height="40">
                <path
                  d="M12 4 L12 20 M6 14 L12 20 L18 14"
                  stroke="rgb(60, 50, 40)"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          <div className="upgrade-options">
            {upgradeOffer.options.map((card) => (
              <div
                key={card.name}
                className={`upgrade-option ${selectedCard === card.name ? 'selected' : ''}`}
                onClick={() => handleCardClick(card.name)}
              >
                <div className="upgrade-option-card">
                  <div className="upgrade-card-title" style={{ backgroundColor: '#7c9885' }}>
                    {formatCardName(card.name)}
                  </div>
                  <div className="upgrade-card-artwork">
                    <img
                      src={assetUrl(`/assets/cards/${card.name}.png`)}
                      alt={card.name}
                      className="upgrade-card-image"
                    />
                  </div>
                  <TierIndicator tier={card.tier ?? 'tier_2'} usageCount={0} />
                </div>
                <div className="upgrade-option-info">
                  <p className="upgrade-option-description">{card.description}</p>
                </div>
                {selectedCard === card.name && (
                  <div className="upgrade-selected-indicator">
                    <span>✓</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="upgrade-actions">
          <button
            className="upgrade-confirm-button"
            onClick={handleConfirm}
            disabled={!selectedCard}
          >
            Add to Deck
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
